import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { BoOption } from '../../shared/types'
import LayerThumb from './LayerThumb'
import OptionActionsMenu from './OptionActionsMenu'
import type { OptionAction } from './OptionActionsMenu'

export interface OptionCardProps {
  option: BoOption
  unlocksAt: number | null
  canMoveEarlier?: boolean
  canMoveLater?: boolean
  onAction?: (action: OptionAction) => void
}

export default function OptionCard({
  option,
  unlocksAt,
  canMoveEarlier = false,
  canMoveLater = false,
  onAction,
}: OptionCardProps) {
  const { formatMessage } = useIntl()
  const retired = option.status === 'retired'

  return (
    <Card
      variant="outlined"
      sx={{ opacity: retired ? 0.7 : 1, height: '100%' }}
      data-testid={`avatar-option-${option.slug}`}
    >
      {onAction && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pl: 0.5,
            pr: 0.5,
            pt: 0.5,
          }}
        >
          <Stack direction="row" spacing={0.25}>
            {!retired && (
              <>
                <IconButton
                  size="small"
                  disabled={!canMoveEarlier}
                  aria-label={formatMessage(
                    { id: 'avatarStudio.actions.moveEarlierNamed' },
                    { name: option.displayName },
                  )}
                  onClick={() => onAction('earlier')}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!canMoveLater}
                  aria-label={formatMessage(
                    { id: 'avatarStudio.actions.moveLaterNamed' },
                    { name: option.displayName },
                  )}
                  onClick={() => onAction('later')}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </>
            )}
          </Stack>
          <OptionActionsMenu option={option} onAction={onAction} />
        </Box>
      )}
      <Stack alignItems="center" sx={{ pt: onAction ? 0 : 2 }}>
        <LayerThumb
          layers={option.layers}
          alt={option.displayName}
          dimmed={retired}
        />
      </Stack>
      <CardContent sx={{ pb: 2 }}>
        <Typography variant="body2" noWrap={true} title={option.displayName}>
          {option.displayName}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
          {retired && (
            <Tooltip
              title={formatMessage({ id: 'avatarStudio.option.retiredExplained' })}
            >
              <Chip
                size="small"
                tabIndex={0}
                label={formatMessage({ id: 'avatarStudio.option.status.retired' })}
              />
            </Tooltip>
          )}
          {unlocksAt !== null && (
            <Tooltip
              title={formatMessage(
                { id: 'avatarStudio.option.unlocksAtExplained' },
                { tier: unlocksAt },
              )}
            >
              <Chip
                size="small"
                variant="outlined"
                tabIndex={0}
                label={formatMessage(
                  { id: 'avatarStudio.option.tierBadge' },
                  { tier: unlocksAt },
                )}
              />
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
